import { useCallback, useEffect, useState } from 'react';
import { TV2M_DEEP_LINK_PATH } from '@apm/contracts';
import type { BloomRuntimeConfig, DeferredLinkResult, Tv2mLinkContext } from '@apm/contracts';
import { config, runtimeOverrides } from './config.js';
import { loadLastContext, saveLastContext } from './last-context.js';
import { unlockBuffers } from './playback-buffers.js';
import { resolvePage } from './routing.js';
import { fetchRuntimeConfig } from './runtime-config.js';
import { useAuth } from './auth/use-auth.js';
import type { AuthApi } from './auth/use-auth.js';
import { takeJustLoggedIn } from './auth/session.js';
import { unlinkBloomDeviceAccount } from './bloom-device.js';
import { getNativeBridge } from './native/bridge.js';
import { useTv2m } from './useTv2m.js';
import { useTvFocus } from './tv-focus/use-tv-focus.js';
import { useBrowserBack } from './browser-back.js';
import { useTouchInput } from './touch-input.js';
import { useEntryFullscreen } from './use-entry-fullscreen.js';
import { AppHeader } from './components/AppHeader.js';
import { ChannelGate } from './screens/ChannelGate.js';
import { DebugPage } from './pages/DebugPage.js';
import { InstallPage } from './pages/InstallPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { ProductPage } from './pages/ProductPage.js';
import { PurchaseDonePage } from './pages/PurchaseDonePage.js';
import { StbSimPage } from './pages/StbSimPage.js';
import { useT } from './i18n/LocaleProvider.js';

/**
 * 부팅 1단계 — 런타임 설정.
 *
 * **`GET /v1/bloom/config` 가 다른 모든 것보다 먼저다** (`docs/14` B8 의 부팅 순서 1번).
 * 모드·VAPID 공개키·구글 클라이언트 ID·TV2M 주소를 모르면 SDK 도 로그인 화면도 세울 수
 * 없다. 그래서 이 컴포넌트는 **훅을 하나도 더 부르지 않고** 다음 단계를 자식으로 넘긴다 —
 * 자식이 마운트되는 시점이 곧 "설정이 준비된 시점" 이라야 순서가 코드로 강제된다.
 */
export function App() {
  const t = useT();
  /*
   * TV 리모컨 포커스 (v10). **여기가 그 리스너의 자리다.**
   *
   * `ChannelGate` 보다 위라 채널 전환에 언마운트되지 않는다 — 절대 규칙 27 의 누수 경로
   * 자체가 없다. 층은 React 상태가 아니라 DOM(`[data-focus-scope]`)에서 읽으므로 이 훅은
   * 시청 화면의 어떤 값도 필요로 하지 않고, 그래서 이 높이에 설 수 있다.
   *
   * `/sim/stb` 도 덮는다(아래 분기보다 먼저다) — 셋톱 모사 화면이야말로 리모컨으로
   * 조작되는 것이 맞고, 그 화면은 사전을 쓰지 않으므로 새 문구가 생기지도 않는다.
   *
   * **TV 계열이 아니면 리스너조차 붙지 않는다** (`docs/25` §6).
   */
  useTvFocus();
  // 시스템 「이전」(Android PWA)이 시트를 닫는다 — ESC 와 같은 길 (`browser-back.ts`)
  useBrowserBack();
  // 손가락이 닿은 문서에는 호버가 없다 (`touch-input.ts` · `styles.css` 의 호버 블록)
  useTouchInput();
  /*
   * 노트북 앱은 **첫 조작에서** 전체화면으로 간다 (2026-09-16 · `entry-fullscreen.ts`).
   *
   * **제스처 없이는 못 한다** — 그래서 「켜자마자」의 실제 뜻이 「첫 조작에서」이고,
   * 문(홈 OK·채널 카드·숫자 키·옛 게이트·ONAIR)을 열거하지 않으려고 여기 선다.
   * 브라우저 탭과 폰에는 **리스너조차 붙지 않는다.**
   */
  useEntryFullscreen();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; runtime: BloomRuntimeConfig }
    | { kind: 'error'; message: string | null }
  >({ kind: 'loading' });

  useEffect(() => {
    let alive = true;
    void fetchRuntimeConfig(config.serverUrl, runtimeOverrides)
      .then((runtime) => {
        if (alive) setState({ kind: 'ready', runtime });
      })
      .catch((error: unknown) => {
        if (alive) setState({ kind: 'error', message: describeError(error) });
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state.kind === 'ready') {
    /*
     * STB 모사는 **로그인 게이트 앞에 있다** (B11 실기에서 고쳤다).
     *
     * 이 화면은 BLOOM 이 아니라 다른 사업자의 셋톱 앱을 흉내 낸 것이고, 위탁형(Mode A)의
     * 요점 하나가 **계정을 요구하지 않는 사업자 정책**이다 (`docs/12` §2.2). 그것을
     * BLOOM 의 로그인 뒤에 두면 「계정 없는 경로를 보려면 계정이 필요하다」가 되고,
     * 데모에서 그 모순이 그대로 드러난다 (`docs/11` §2 의 장면 7).
     *
     * 게이트를 지나지 않아도 안전하다 — 이 화면이 부르는 것은 공개 엔드포인트 둘
     * (`GET /v1/creatives/{id}` · 무인증 `POST /v1/trigger`)뿐이고, 노출 자체는 배포
     * 설정이 가른다 (`routing.ts`).
     *
     * 경로를 `window.location` 에서 한 번만 읽는다. 이 화면에는 앱 안 이동이 없다.
     */
    const entry = resolvePage(window.location.pathname, {
      debug: config.debug,
      modeASim: config.enableModeASim,
    });
    if (entry === 'stb-sim') {
      return (
        <div className="app">
          <StbSimPage runtime={state.runtime} />
        </div>
      );
    }
    return <AuthGate runtime={state.runtime} />;
  }

  /*
   * **폴백 기본값으로 앱을 띄우지 않는다.** 그것이 곧 「값이 두 곳에 있는」 상태이고,
   * 폴백으로 뜬 앱은 잘못된 VAPID 키로 구독을 만들어 놓고 멀쩡해 보인다 (절대 규칙 19).
   * 뜨지 않고 이유를 말하는 편이 낫다.
   */
  return (
    <div className="app">
      <AppHeader />
      <div className="card">
        {state.kind === 'loading' ? (
          <p className="card__text">{t('common.loading')}</p>
        ) : (
          <>
            <h2 className="card__title">{t('boot.configFailed')}</h2>
            <p className="card__text">{state.message ?? t('common.unknownError')}</p>
            <p className="card__text">{t('boot.server', { url: config.serverUrl })}</p>
            <div style={{ marginTop: '0.9rem' }}>
              <button
                type="button"
                className="button"
                onClick={() => {
                  window.location.reload();
                }}
              >
                {t('common.retry')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 부팅 실패의 원인 한 줄. **사전을 거치지 않는다** — 여기 오는 것은 네트워크·CORS 처럼
 * 개발자가 읽어야 하는 원문이고, 번역하면 오히려 원인을 찾기 어려워진다
 * (`errorMessage` 를 원문 그대로 두는 것과 같은 판단).
 * 원인을 알 수 없을 때만 화면이 `common.unknownError` 로 대신한다.
 */
function describeError(error: unknown): string | null {
  if (error instanceof Error) return error.message;
  return null;
}

/**
 * 부팅 2·3단계 — 로그인 확인.
 *
 * **이 컴포넌트가 부팅 순서를 코드로 강제한다.** `useTv2m` 을 부르는 `Shell` 은 여기서
 * 통과한 뒤에야 마운트되므로, SDK 기동이 로그인보다 먼저 일어날 수 없다.
 *
 * 순서를 지켜야 하는 이유는 하나다 (`docs/12` §8 ④). 기동은 모듈 스코프 약속이라 페이지당
 * 한 번뿐인데, 그 안의 `checkDeferredLink()` 가 **살아 있는 링크를 첫 호출에서 소비한다**
 * (`consumedAt`, T5). 로그인 리다이렉트로 페이지가 리로드되면 모듈은 새로 평가되지만
 * **소비된 링크는 되살아나지 않는다** — QR 로 온 사람이 로그인하고 돌아왔을 때 상품이
 * 없는 것이 그 증상이다.
 *
 * 주석으로 적어 두는 대신 컴포넌트 경계로 나눈 이유가 이것이다. 순서는 지켜지거나
 * 지켜지지 않거나인데, 지켜지지 않았을 때의 증상이 조용하다.
 */
function AuthGate({ runtime }: { runtime: BloomRuntimeConfig }) {
  const t = useT();
  const auth = useAuth(runtime);

  // 토큰은 있는데 아직 확인하지 못한 상태다. 여기서 로그인 화면을 그리면 새로고침마다
  // 그것이 한 번 번쩍인다 (콘솔의 `checking` 과 같은 자리 — `docs/06` A).
  if (auth.status === 'checking') {
    return (
      <div className="app">
        <AppHeader />
        <div className="card">
          <p className="card__text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  /*
   * **주소를 바꾸지 않는다.** `docs/14` B8 은 "→ /login" 이라고 적었지만, 옮기면 원래
   * URL 이 히스토리에서 사라져 `returnTo` 하나에 전부를 걸게 된다 — 저장소가 막힌 환경
   * (사파리 프라이빗)에서는 그 하나마저 없다. 자리에 그대로 두면 새로고침이 같은 곳으로
   * 돌아오고, `?ref=` 는 주소창에 남아 있다. `returnTo` 는 그대로 보관하되 **두 번째
   * 안전망**이 된다.
   */
  if (runtime.requireLogin && auth.status !== 'authenticated') {
    return (
      <div className="app">
        <AppHeader />
        <LoginPage runtime={runtime} auth={auth} />
      </div>
    );
  }

  return <Shell runtime={runtime} auth={auth} />;
}

/**
 * 알림에서 온 목적지로 옮긴다. **유입 통로 둘이 같은 자리를 쓴다** — Service Worker
 * (웹·PWA)와 네이티브 셸(iOS)이다.
 *
 * `pushState` 가 아니라 **전체 이동**인 이유가 둘이다. 쿼리에 유입 컨텍스트가 들어 있어
 * 그것을 잃으면 알림에서 온 사람이 상품 화면 대신 방송을 보고, 무엇보다 **기동이 다시
 * 돌아야 `push_clicked` 가 나간다** — 그 이벤트는 SDK 가 `source=tv2m` 를 보고 내는
 * 것이라(`ENTRY_EVENTS`) 화면 안에서 옮기면 유입이 기록에서 사라진다.
 */
function openFromNotification(url: string): void {
  window.location.href = url;
}

/**
 * BLOOM 본체. **CTV·PC·모바일을 가리지 않는 같은 앱이다** (CLAUDE.md 머리).
 *
 * 진입점이 다섯이다 — 시청(`/`), Deep Link(`/tv2m/product`), QR 설치 안내(`/install`),
 * 구매 완료(`/done`), 진단(`/debug`). 우선순위는 `resolvePage` 와 SDK 코어가 나눠 정한다:
 * 경로는 여기서, **Deep Link 와 설치 ref 중 무엇이 이기는지는 코어**(`sdk.route()`)에서.
 *
 * ── 시청 화면은 초기화를 기다리지 않는다 ──────────────────────
 *
 * 흡수된 모바일 웹은 `tv2m.ready` 전에 "준비 중…" 을 그렸다. 통합 뒤 그 규칙을 시청
 * 화면까지 넓히면 **재생 시작이 TV2M 서버 왕복 뒤로 밀린다** — tv2m-server 가 꺼져 있으면
 * 그만큼 늦고, 그것은 이 앱이 재는 수치(T_freeze·진입 지연)를 바꾼다. 시청은 TV2M 을
 * 필요로 하지 않으므로(트리거를 누르기 전까지) 기다리지 않는다.
 */
function Shell({ runtime, auth }: { runtime: BloomRuntimeConfig; auth: AuthApi }) {
  const t = useT();
  // **토큰이 기기 등록에 실린다** — 그래야 `BloomDevice.userId` 가 채워지고 Tier 1 이
  // 이 기기를 찾는다 (`docs/14` B6 의 1번). 로그인 확인이 먼저인 이유 중 하나다.
  const tv2m = useTv2m(runtime, auth.token);
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [manual, setManual] = useState<Tv2mLinkContext | null>(null);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState<DeferredLinkResult | null>(null);

  /** 앱 안 이동. 라우터를 넣지 않았으므로 `pushState` + 상태 하나가 전부다. */
  const navigate = useCallback((to: string) => {
    window.history.pushState(null, '', to + window.location.search);
    setPathname(new URL(to, window.location.origin).pathname);
  }, []);

  // 뒤로 가기. 없으면 구매 완료에서 뒤로 눌렀을 때 주소만 바뀌고 화면이 그대로다.
  useEffect(() => {
    const onPop = () => {
      setPathname(window.location.pathname);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  /**
   * Service Worker 가 알림 클릭을 알려 온다.
   *
   * 이미 열린 탭이 있으면 SW 는 새 창을 열지 않고 그것을 focus 한 뒤 목적지를
   * `postMessage` 로 넘긴다 (`public/sw.js`). **이동은 페이지가 한다** —
   * `client.navigate()` 는 SW 가 제어 중인 창에서만 되고 A2HS 로 설치된 창은 제어
   * 밖일 수 있다.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (typeof data !== 'object' || data === null) return;
      const message = data as { type?: unknown; url?: unknown };
      if (message.type !== 'tv2m:navigate' || typeof message.url !== 'string') return;
      openFromNotification(message.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, []);

  /**
   * 네이티브 셸이 알림 탭을 알려 온다 (v12, `docs/29` D13 의 `onDeepLink`).
   *
   * **위 SW 경로와 같은 자리·같은 처리다.** iOS 네이티브에는 Service Worker 가 없어
   * 통로만 다르고, 도착하는 것은 똑같이 딥링크 URL 하나다 — 두 유입이 같은 코드를
   * 지나야 「알림으로 들어온 화면」이 하나로 유지된다.
   *
   * **떼지 않는다.** 셸의 구독은 문서 수명과 같고(userScript 가 문서마다 새로 심는다),
   * 무엇보다 이 핸들러가 붙는 순간 **차가운 기동에서 붙들어 둔 링크가 곧바로 온다**
   * (`docs/29` 함정 ⑪) — 효과 정리에서 떼면 `StrictMode` 의 두 번째 마운트가 그것을
   * 이미 소비한 뒤라 아무 데도 가지 않는다.
   */
  useEffect(() => {
    getNativeBridge()?.onDeepLink(openFromNotification);
  }, []);

  // 이 기기가 마지막으로 본 상품을 보관한다 (`last-context.ts` — T5 가 남긴 Tier 0
  // 복원 부채를 갚는 자리). 서버 조회를 대체하지 않는다.
  const context = manual ?? tv2m.context;
  useEffect(() => {
    if (context !== null) saveLastContext(context);
  }, [context]);

  /** 설치 화면의 "지금 확인". 두 번째 이후 방문에서만 뜻이 있다 (첫 실행은 이미 조회했다). */
  const checkNow = useCallback(() => {
    if (tv2m.sdk === null) return;
    setChecking(true);
    void tv2m.sdk
      .checkDeferredLink()
      .then((result) => {
        setChecked(result);
        if (result.found && result.productId && result.campaignId) {
          setManual({
            productId: result.productId,
            campaignId: result.campaignId,
            source: result.source ?? 'tv2m_deferred',
            triggerType: result.triggerType ?? 'channel_content',
            ...(result.matchId ? { matchId: result.matchId } : {}),
            ...(result.couponCode ? { coupon: result.couponCode } : {}),
          });
          navigate(TV2M_DEEP_LINK_PATH);
        }
      })
      .finally(() => {
        setChecking(false);
      });
  }, [tv2m.sdk, navigate]);

  /**
   * 로그인 직후 첫 기동에서 한 번 낸다 (`docs/13` §8).
   *
   * **로그인 순간에는 낼 수 없다.** 그때는 SDK 가 아직 서지 않았고(부팅 순서상 로그인이
   * 먼저다) 곧 페이지를 다시 연다. 표식을 남겨 두었다가 기동이 끝난 여기서 낸다.
   */
  useEffect(() => {
    if (!tv2m.ready) return;
    if (takeJustLoggedIn()) tv2m.trackEvent('account_linked');
  }, [tv2m.ready, tv2m.trackEvent]);

  /**
   * 로그아웃. 순서가 있다.
   *
   * 1. **이벤트를 먼저 내고 보낸다.** 다음 단계가 토큰을 버리고 페이지를 옮기므로,
   *    여기서 보내지 않으면 그 기록이 큐에 남은 채 사라진다.
   * 2. 이 기기를 계정에서 뗀다. **구독은 남긴다** — 알림을 끈 것이 아니라 계정을 뗀
   *    것이고, 이 기기는 여전히 Tier 2·4 의 대상이다 (`docs/15` §3-4).
   * 3. 토큰을 버리고 다시 연다.
   *
   * 2번이 실패해도 3번은 한다. 서버가 못 떼면 이 기기는 계정에 묶인 채 남지만,
   * **손에 든 기기에서 로그아웃이 안 되는 것이 더 나쁘다.** 다음 로그인이 그 자리를 덮는다.
   */
  const logout = useCallback(async () => {
    tv2m.trackEvent('account_unlinked');
    await tv2m.flushEvents();

    const deviceId = tv2m.init?.deviceId;
    if (deviceId !== undefined && auth.token !== null) {
      try {
        await unlinkBloomDeviceAccount({
          serverUrl: config.serverUrl,
          deviceId,
          token: auth.token,
        });
      } catch {
        /* 만료된 토큰이거나 서버가 없다. 로그아웃 자체는 성립해야 한다 */
      }
    }

    await auth.logout();
  }, [tv2m.trackEvent, tv2m.flushEvents, tv2m.init, auth]);

  const openLast = useCallback(() => {
    const last = loadLastContext();
    if (last === null) return;
    setManual(last);
    navigate(TV2M_DEEP_LINK_PATH);
  }, [navigate]);

  const page = resolvePage(pathname, {
    debug: config.debug,
    // 꺼져 있으면 `/sim/stb` 는 없는 경로처럼 군다 (`routing.ts`). B11.
    modeASim: config.enableModeASim,
  });

  /**
   * **시청 화면은 통째로 다른 레이아웃이다.** 아래 `.app` 은 손에 들고 읽는 문서이고
   * 이쪽은 전체화면 비디오 위의 오버레이 레이어다 (`docs/06` 「화면 구조」).
   * 헤더도 씌우지 않는다 — 그것이 오버레이 좌표를 밀어낸다.
   */
  /**
   * **시청 화면은 통째로 다른 레이아웃이다.** 아래 `.app` 은 손에 들고 읽는 문서이고
   * 이쪽은 전체화면 비디오 위의 오버레이 레이어다 (`docs/06` 「화면 구조」).
   * 헤더도 씌우지 않는다 — 그것이 오버레이 좌표를 밀어낸다.
   *
   * **v9 부터 채널 판정이 그 앞에 선다** (`ChannelGate`). 채널이 정해지기 전에는
   * 시청 화면이 마운트되지 않으므로 리시버가 채널 없이 설 수 없다 — `AuthGate` 가
   * 로그인 순서를 컴포넌트 경계로 강제한 것과 같은 방침이다.
   */
  if (page === 'watch' || page === 'channels') {
    return (
      <ChannelGate
        runtime={runtime}
        tv2m={tv2m}
        /*
         * **통째로 넘긴다.** 시청 화면이 이것을 셋으로 쓴다 — 핸드오프에 실을 토큰,
         * 설정 시트의 계정 줄, 그리고 **익명일 때의 로그인 입구**(`docs/21` W10).
         */
        auth={{ ...auth, logout }}
        lastContext={loadLastContext()}
        onOpenLast={openLast}
        /*
         * ★ **시청 화면의 시트에는 완료 화면으로 가는 문이 없다** (v21/F13).
         *
         * 전에는 여기서 `/done` 으로 옮겼다. 근거는 「판매처를 새 탭으로 연 뒤에만
         * 닿는다」였는데, **그 판정이 아이폰 홈 화면 앱에서 성립하지 않는다** —
         * `window.open` 이 truthy 를 주고도 아무것도 열리지 않을 수 있고, 그러면
         * **나가지도 않은 사람에게 「구매가 완료되었습니다」를 보여 준다.** 그 주석이
         * 막으려던 바로 그 일을 그 전제가 스스로 하고 있었다.
         *
         * 그리고 옮기는 순간 `WatchScreen` 이 내려가 **방송이 끊긴다** — 규칙 14 가
         * 지키는 「떠나는 유일한 순간은 판매처로 이동」에 완료 화면은 들지 않는다.
         *
         * **딥링크로 상품 화면에 도착한 폰에는 그대로 있다** (아래 `ProductPage`) —
         * 그쪽은 뒤에서 도는 방송이 없다.
         */
        forceList={page === 'channels'}
      />
    );
  }

  // 여기서부터는 초기화 결과가 필요하다. **저장소를 읽기 전이라** "첫 실행인가" 도
  // "Deep Link 가 있는가" 도 아직 모른다 — 여기서 뭔가 그리면 알림으로 들어온 사람에게
  // 엉뚱한 화면이 한 번 번쩍인다 (콘솔의 `checking` 상태와 같은 자리다).
  return (
    <div className="app">
      <AppHeader />
      {tv2m.ready ? (
        renderPage()
      ) : (
        <div className="card">
          <p className="card__text">{t('common.loading')}</p>
        </div>
      )}
    </div>
  );

  function renderPage() {
    /*
     * 로그인돼 있는(또는 로그인이 필요 없는) 사람이 `/login` 으로 직접 들어온 경우다 —
     * 북마크, 로그아웃 뒤 뒤로 가기. 로그인 화면은 할 말이 없으므로 방송으로 안내한다.
     * **자동으로 옮기지 않는다** — 주소를 손으로 넣은 사람을 이유 없이 밀어내지 않는
     * 것이 이 앱의 방침이다 (아래 「상품 정보가 없습니다」와 같다).
     */
    if (page === 'login') {
      return (
        <div className="card">
          <h2 className="card__title">{t('login.alreadySignedIn')}</h2>
          <div style={{ marginTop: '0.9rem' }}>
            <button
              type="button"
              className="button"
              onClick={() => {
                navigate('/');
              }}
            >
              {t('common.watchBroadcast')}
            </button>
          </div>
        </div>
      );
    }

    if (page === 'debug') {
      return <DebugPage state={tv2m} sdk={tv2m.sdk} runtime={runtime} />;
    }

    if (page === 'done') {
      return (
        <PurchaseDonePage
          sdk={tv2m.sdk}
          context={context}
          onGoHome={() => {
            navigate('/');
          }}
        />
      );
    }

    // 설치 안내는 **컨텍스트를 이미 복원했으면 건너뛴다.** 복원된 사람에게 설치를
    // 다시 권하면 그가 보러 온 상품이 한 화면 뒤로 밀린다.
    if (page === 'install' && context === null) {
      return (
        <InstallPage
          firstRun={tv2m.init?.firstRun ?? false}
          deferred={checked ?? tv2m.deferred}
          onCheckNow={checkNow}
          checking={checking}
        />
      );
    }

    if (context !== null) {
      return (
        <ProductPage
          context={context}
          checkout={tv2m}
          // 광고로 들어온 화면이 소재를 읽을 곳 (B11). 채널 콘텐츠면 쓰이지 않는다.
          tv2mServerUrl={runtime.tv2mServerUrl}
          /*
           * **여기에는 시청 세션이 없다.** Push·QR·딥링크로 곧장 들어온 화면이라 리시버가
           * 뜬 적이 없고, 그래서 `d=`·`pl=` 이 빈 채로 나간다. 없는 값을 만들어 붙이지
           * 않는다 — 이 경로의 추적은 규칙 8 의 서버측 역참조(`match_id`)가 맡는다
           * (`checkout.ts` 의 `CheckoutTracking`).
           */
          tracking={{ deviceId: '', playlistId: null }}
          onContinue={() => {
            /*
             * **이 클릭이 제스처다** (v19). 알림·QR 로 들어온 이 화면은 전체 이동이라
             * 문서가 새것이고 `<video>` 한 쌍에 재생 허가가 없다 — 그대로 두면 방송으로
             * 돌아갔을 때 진입 게이트가 한 번 더 뜬다(`start()` 는 `useEffect` 안이라
             * 제스처 밖이다). 여기서 심어 두면 그 게이트가 사라진다.
             */
            unlockBuffers();
            navigate('/');
          }}
          continueLabelKey="common.watchBroadcast"
          onPurchaseDone={() => {
            navigate('/done');
          }}
        />
      );
    }

    // 컨텍스트 없이 상품·설치 경로로 들어온 경우다 (링크가 만료됐거나 주소를 손으로
    // 넣었거나). 홈이 없어졌으므로 방송으로 되돌린다 — **자동으로 옮기지는 않는다.**
    // 알림을 눌러 온 사람이 이유도 모른 채 다른 화면으로 밀려나는 것이 더 나쁘다.
    return (
      <div className="card">
        <h2 className="card__title">{t('product.missingTitle')}</h2>
        <p className="card__text">{t('product.missingText')}</p>
        <div style={{ marginTop: '0.9rem' }}>
          <button
            type="button"
            className="button"
            onClick={() => {
              // 위와 같은 이유다 — 이 화면도 전체 이동으로 들어온 새 문서다.
              unlockBuffers();
              navigate('/');
            }}
          >
            {t('common.watchBroadcast')}
          </button>
        </div>
      </div>
    );
  }
}
